// Puja services data, customer reviews, and spiritual testimonials

export interface Service {
  id: string;
  name: string;
  description: string;
  image: string;
  priceRange: string;
  category: string;
  // Hero section fields (optional – activates split hero layout)
  heroTagline?: string;
  heroTitle?: string;
  heroDescription?: string;
  heroRating?: number;       // e.g. 4.8 – drives dynamic star display
  familiesServed?: string;
  serviceModes?: string[];
  startingPrice?: string;
  announcementText?: string;
}

export interface WhyBookPoint {
  title: string;
  description: string;
}

export interface SubService {
  id: number;
  name: string;
  icon: string;
  image: string;
  description: string;
  price: string;
  duration: string;
  rating: number;
  bookingUrl: string;
  // Enhanced plan details
  priests?: number;
  materials?: string[];
  includes?: string[];
  ritualSteps?: string[];
  bestFor?: string[];
  languages?: string[];
  specialFeatures?: string[];
  minAdvanceBooking?: string;
  cancellationPolicy?: string;
  platformSitePreferred?: boolean; // Flag to indicate if platform site is preferable
  platformSiteMessage?: string; // Custom message for platform site preference
  siteOnly?: boolean; // When true the service can ONLY be performed at the Antaryatra site — home option is locked out
  siteOnlyMessage?: string; // Explanation shown when user tries to toggle off the site option
  showServiceTrustSection?: boolean;
  serviceTrustBadgeText?: string;
  serviceTrustSummaryBadges?: string[];
  whyBookWithAntaryatraPoints?: Array<WhyBookPoint | string>;
}

export interface Review {
  id: number;
  customerName: string;
  rating: number;
  comment: string;
  date: string;
  avatar: string;
}

export interface Testimonial {
  id: number;
  customerName: string;
  photo: string;
  testimonial: string;
  service: string;
}

export interface RecommendedService {
  id: number;
  name: string;
  image: string;
  price: string;
  rating: number;
  trending?: boolean;
}

export interface ServiceBenefit {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export interface Promotion {
  id: number;
  title: string;
  description: string;
  discount: string;
  validUntil: string;
  code: string;
}

// Main Services Data
export const mainServices: Service[] = [
  {
    id: 'shiv-pujan',
    name: 'Shiv Pujan',
    description: 'Comprehensive Shiv rituals for peace, protection, and longevity',
    image: '/images/puja_service.jpg',
    priceRange: '₹15,000 - ₹40,000',
    category: 'Spiritual'
  },
  {
    id: 'devi-pujan',
    name: 'Devi Pujan',
    description: 'Divine worship of goddess shakti for power, protection, and prosperity',
    image: '/images/services/durga.jpg',
    priceRange: '₹25,000 - ₹55,000',
    category: 'Wellness',
    heroTagline: 'Seeking Divine Protection, Inner Strength or Life-Changing Blessings?',
    heroTitle: 'DEVI PUJAN FOR POWER, PROTECTION & PROSPERITY',
    heroDescription: 'Invoke the divine energy of Goddess Shakti. Remove negativity, overcome enemies and attract abundance with authentic Vedic rituals by verified Acharyas.',
    heroRating: 4,
    familiesServed: '500+',
    serviceModes: ['🏠 Home', '🛕 Temple', '💻 Online'],
    startingPrice: '₹18,000',
    announcementText: 'Navratri Special Puja | Auspicious Muhurat Available – Book Early'
  },
  {
    id: 'navagrah-pujan',
    name: 'Navagrah Pujan',
    description: 'Powerful rituals to appease the nine planets for life balance',
    image: '/images/services/nav_grah.jpg',
    priceRange: '₹8,000 - ₹40,000',
    category: 'Guidance',
    heroTagline: '✨ Facing Delays, Obstacles, Career or Health Issues?',
    heroTitle: 'NAVAGRAH PUJAN FOR COMPLETE PLANETARY BALANCE',
    heroDescription: 'Strengthen weak planets. Remove doshas. Restore life harmony with verified Vedic Acharyas.',
    heroRating: 4.6,
    familiesServed: '1,000+',
    serviceModes: ['🏠 Home', '🛕 Temple', '💻 Online'],
    startingPrice: '₹15,000',
    announcementText: 'Auspicious Muhurat This Week | Book Early'
  },
  {
    id: 'vishist-sanskar',
    name: 'Vishist Sanskar',
    description: 'Complete lifecycle ceremonies including naming, first feeding, sacred thread ceremony and traditional Hindu wedding rituals',
    image: '/images/services/vishist_sanskar.jpg',
    priceRange: '₹10,000 - ₹75,000',
    category: 'Sanskar'
  },
  {
    id: 'shanti-samridhi',
    name: 'Shanti and Samridhi Pujan',
    description: 'Sacred ceremonies for peace, prosperity and spiritual well-being including home entry and devotional rituals',
    image: '/images/services/satyanarayan.jpg',
    priceRange: '₹8,000 - ₹20,000',
    category: 'Peace & Prosperity'
  },
  {
    id: 'dosh-nivaran',
    name: 'Dosh Nivaran Pujan',
    description: 'Remedial ceremonies to neutralize astrological doshas and negative planetary influences for life harmony',
    image: '/images/services/kaal_sarp.jpg',
    priceRange: '₹15,000 - ₹35,000',
    category: 'Remedial'
  },
  {
    id: 'shradh-karm',
    name: 'Shradh Karm',
    description: 'Sacred ancestral rituals and Shradh ceremonies for spiritual liberation and blessings of departed souls',
    image: '/images/services/shradh_karm.jpg',
    priceRange: '₹20,000 - ₹50,000',
    category: 'Ancestral Rites'
  },
  {
    id: 'ramayan-sunder-kand',
    name: 'Ramayan & Sunder Kand',
    description: 'Sacred recitation of Ramayana and Hanuman devotional ceremonies for divine blessings and obstacle removal',
    image: '/images/services/ram_darbar.jpg',
    priceRange: '₹8,000 - ₹25,000',
    category: 'Devotional'
  }
];

// Sub Services Data
export const subServicesData: Record<string, SubService[]> = {
  'shiv-pujan': [
    {
      id: 1,
      name: 'Maha Mrityunjaya Pujan',
      icon: '🔱',
      image: '/images/maha_mritunjay.jpg',
      description: 'Comprehensive Shiv rituals for peace, protection, and longevity',
      price: '₹21,500',
      duration: '1-5 days*',
      rating: 5,
      bookingUrl: '/booking/shiv-pujan/maha-mrityunjaya',
      priests: 3,
      materials: ['Rudraksha', 'Sacred Water', 'Flowers', 'Incense', 'Ghee', 'Rice'],
      includes: ['3 qualified priests', 'All puja materials', 'Sacred offerings', 'Digital photos', 'Completion certificate'],
      ritualSteps: ['Ganesh Invocation', 'Kalash Sthapana', 'Maha Mrityunjaya Jaap', 'Abhishekam', 'Aarti', 'Prasad Distribution'],
      bestFor: ['Health issues', 'Protection from accidents', 'Longevity', 'Overcoming fear of death'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Vedic chanting', '108 times mantra recitation', 'Sacred ash prasad'],
      minAdvanceBooking: '3 days',
      platformSitePreferred: true,
      cancellationPolicy: '24 hours before ceremony'
    },
    {
      id: 2,
      name: 'Rudrabhishekh',
      icon: '🕉️',
      image: '/images/rudrabhishek.jpg',
      description: 'Complete Rudra Ashtadhyayi Paath with Shodashopachaar, Personal Sankalp & Shivling Shringar',
      price: '₹25,000',
      duration: '4-6 hours',
      rating: 5,
      bookingUrl: '/booking/shiv-pujan/rudrabhishekh',
      priests: 2,
      materials: ['Milk', 'Honey', 'Yogurt', 'Ghee', 'Sacred Water', 'Bilwa Leaves'],
      includes: ['2 experienced priests', 'All abhishek materials', 'Bilwa leaves', 'Sacred vibhuti', 'Blessed water'],
      ritualSteps: ['Sankalp', 'Rudra Path', 'Milk Abhishekam', 'Honey Abhishekam', 'Water Abhishekam', 'Final Aarti'],
      bestFor: ['Spiritual cleansing', 'Removing negative energy', 'Peace of mind', 'Family harmony'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Traditional chanting', 'Multiple abhishekams', 'Sacred ash distribution'],
      minAdvanceBooking: '2 days',
      platformSitePreferred: true,
      cancellationPolicy: '12 hours before ceremony',
      showServiceTrustSection: true,
      serviceTrustBadgeText: 'Participate from Anywhere in the World',
      serviceTrustSummaryBadges: [],
      whyBookWithAntaryatraPoints: [
        {
          title: 'Experienced Kashi Acharyas',
          description: ''
        },
        {
          title: '200+ Rudrabhishek Performed',
          description: ''
        },
        {
          title: 'Home or Kashi option',
          description: ''
        },
        {
          title: 'Complete Arrangements',
          description: ''
        },
        {
          title: ' Dedicated Support',
          description: ''
        }
      ]
    },
    {
      id: 3,
      name: 'Maha Rudrai Anushthaan',
      icon: '🔱',
      image: '/images/puja_service.jpg',
      description: 'Extensive Rudra path for spiritual cleansing and blessings',
      price: '₹85,000',
      duration: '1-7 days*',
      rating: 4,
      bookingUrl: '/booking/shiv-pujan/maha-rudra-anushthaan',
      priests: 5,
      materials: ['Sacred Thread', 'Lotus Flowers', 'Sandalwood', 'Camphor', 'Sacred Fire', 'Offerings'],
      includes: ['5 learned priests', 'All ceremonial materials', 'Havan setup', 'Prasad for family', 'Video recording'],
      ritualSteps: ['Gauri Ganesh Puja', 'Kalash Sthapana', 'Rudra Path Chanting', 'Havan Ceremony', 'Purnahuti', 'Blessings'],
      bestFor: ['Major life transitions', 'Business success', 'Spiritual advancement', 'Family welfare'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Complete Rudra recitation', 'Sacred fire ceremony', 'Group chanting'],
      minAdvanceBooking: '7 days',
      platformSitePreferred: true,
      cancellationPolicy: '48 hours before ceremony'
    }
  ],
  'devi-pujan': [
    {
      id: 4,
      name: 'Durga Saptshati Path',
      icon: '🕉️',
      image: '/images/devi_pujan/durga_saptsati.jpg',
      description: 'Sacred recitation of 700 verses of Devi Mahatmya for divine mother blessings and protection',
      price: '₹18,000',
      duration: '4-5 hours',
      rating: 5,
      bookingUrl: '/booking/devi-pujan/durga-saptshati',
      priests: 3,
      materials: ['Sacred texts', 'Red flowers', 'Kumkum', 'Turmeric', 'Coconut', 'Sacred fire', 'Incense'],
      includes: ['3 experienced priests', 'Complete Saptshati recitation', 'Divine mother worship', 'Sacred fire ceremony', 'Blessed prasadam'],
      ritualSteps: ['Ganesh Puja', 'Durga invocation', 'Saptshati recitation', 'Havan ceremony', 'Aarti', 'Final blessings'],
      bestFor: ['Divine protection', 'Overcoming enemies', 'Spiritual strength', 'Family safety'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Complete 700 verse recitation', 'Divine mother blessings', 'Protection mantras', 'Sacred fire offerings'],
      minAdvanceBooking: '3 days',
      platformSitePreferred: true,
      cancellationPolicy: '24 hours before ceremony'
    },
    {
      id: 5,
      name: 'Shat Chandi Yagya',
      icon: '🔥',
      image: '/images/devi_pujan/sat_chandi.jpg',
      description: 'Powerful fire sacrifice with 700 offerings to Goddess Durga for ultimate divine blessings',
      price: '₹45,000',
      duration: '3-7 days*',
      rating: 5,
      bookingUrl: '/booking/devi-pujan/sat-chandi-yagya',
      priests: 5,
      materials: ['Sacred fire altar', 'Ghee', 'Sacred herbs', 'Flowers', 'Grains', 'Precious stones', 'Yagya materials'],
      includes: ['5 qualified priests', 'Complete yagya setup', '700 sacred offerings', 'Divine fire ceremony', 'Yagya prasadam'],
      ritualSteps: ['Elaborate setup', 'Devi invocation', 'Fire ceremony', '700 offerings', 'Purnahuti', 'Divine blessings'],
      bestFor: ['Major life challenges', 'Business success', 'Spiritual advancement', 'Divine intervention'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['700 sacred offerings', 'Powerful fire ceremony', 'Complete divine protection', 'Ultimate blessings'],
      minAdvanceBooking: '7 days',
      platformSitePreferred: true,
      cancellationPolicy: '72 hours before ceremony'
    },
    {
      id: 6,
      name: 'Baglamukhi Path',
      icon: '⚡',
      image: '/images/devi_pujan/baglamukhi.webp',
      description: 'Sacred worship of Goddess Baglamukhi for victory over enemies and legal matters',
      price: '₹22,000',
      duration: '3-6 days*',
      rating: 5,
      bookingUrl: '/booking/devi-pujan/baglamukhi-path',
      priests: 2,
      materials: ['Yellow flowers', 'Turmeric', 'Yellow cloth', 'Baglamukhi yantra', 'Mustard oil', 'Sacred fire'],
      includes: ['2 experienced priests', 'Baglamukhi worship', 'Victory mantras', 'Enemy suppression prayers', 'Legal success blessings'],
      ritualSteps: ['Ganesh Puja', 'Baglamukhi invocation', 'Victory mantras', 'Yantra worship', 'Aarti', 'Success prayers'],
      bestFor: ['Legal issues', 'Enemy problems', 'Court cases', 'Victory in competition'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Victory over enemies', 'Legal success mantras', 'Powerful protection', 'Competition success'],
      minAdvanceBooking: '3 days',
      platformSitePreferred: true,
      cancellationPolicy: '24 hours before ceremony'
    }
  ],
  'navagrah-pujan': [
    {
      id: 7,
      name: 'Shani Shanti Pujan',
      icon: '🪐',
      image: '/images/navgrah/shani.webp',
      description: 'Sacred Saturn planet worship to reduce malefic effects and bring stability',
      price: '₹18,000',
      duration: '3-7 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/shani-shanti',
      priests: 2,
      materials: ['Black sesame', 'Mustard oil', 'Iron items', 'Black cloth', 'Blue flowers'],
      includes: ['2 experienced priests', 'All puja materials', 'Shani yantra', 'Sacred tilak', 'Blessed prasadam'],
      ritualSteps: ['Ganesh Invocation', 'Shani Kalash Sthapana', 'Shani Mantra Jaap', 'Oil Abhishekam', 'Aarti', 'Prasad Distribution'],
      bestFor: ['Career obstacles', 'Legal issues', 'Financial problems', 'Delayed marriage'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Traditional Shani mantras', 'Iron donation ceremony', 'Black sesame offerings'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '24 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site, keeping in mind the duration, logistic, cost  and stay for Acharyas.'
    },
    {
      id: 8,
      name: 'Mangal Shanti Pujan',
      icon: '🔴',
      image: '/images/navgrah/mangal.webp',
      description: 'Mars planet pacification ritual for manglik dosha and courage enhancement',
      price: '₹15,000',
      duration: '1-5 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/mangal-shanti',
      priests: 2,
      materials: ['Red coral', 'Red flowers', 'Jaggery', 'Red cloth', 'Copper items'],
      includes: ['2 qualified priests', 'All ritual materials', 'Mangal yantra', 'Coral blessings', 'Sweet prasadam'],
      ritualSteps: ['Ganesh Puja', 'Mangal Kalash Setup', 'Mangal Mantra Chanting', 'Red Flower Offerings', 'Aarti', 'Blessings'],
      bestFor: ['Manglik dosha', 'Marriage delays', 'Courage building', 'Property matters'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Manglik dosha remedies', 'Red coral energizing', 'Copper vessel donations'],
      minAdvanceBooking: '1 day',
      cancellationPolicy: '12 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site, keeping in mind the timing, logistic, cost  and stay for Acharyas.'
    },
    {
      id: 9,
      name: 'Guru Shanti Pujan',
      icon: '🟡',
      image: '/images/navgrah/guru.webp',
      description: 'Jupiter worship for wisdom, knowledge enhancement and spiritual growth',
      price: '₹20,000',
      duration: '3-7 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/guru-shanti',
      priests: 3,
      materials: ['Yellow topaz', 'Turmeric', 'Yellow flowers', 'Gold items', 'Banana offerings'],
      includes: ['3 learned priests', 'Premium materials', 'Guru yantra', 'Wisdom blessings', 'Educational guidance'],
      ritualSteps: ['Elaborate Ganesh Puja', 'Guru Kalash Prana Pratishtha', 'Guru Mantra Jaap', 'Yellow Offerings', 'Knowledge Prayers', 'Final Aarti'],
      bestFor: ['Education success', 'Career advancement', 'Spiritual growth', 'Teacher-student relationships'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Educational success mantras', 'Wisdom enhancement rituals', 'Yellow topaz blessings'],
      minAdvanceBooking: '3 days',
      cancellationPolicy: '48 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    },
    {
      id: 10,
      name: 'Rahu Shanti Pujan',
      icon: '🌑',
      image: '/images/navgrah/rahu.webp',
      description: 'North lunar node pacification to remove confusion and bring clarity',
      price: '₹22,000',
      duration: '3-7 days*',
      rating: 4,
      bookingUrl: '/booking/navgrah/rahu-shanti',
      priests: 3,
      materials: ['Black gram', 'Coconut', 'Blue flowers', 'Hessonite garnet', 'Incense'],
      includes: ['3 experienced priests', 'Special materials', 'Rahu yantra', 'Gemstone blessings', 'Confusion removal prayers'],
      ritualSteps: ['Extended Ganesh Puja', 'Rahu Kalash Installation', 'Rahu Mantra Recitation', 'Black Offerings', 'Clarity Prayers', 'Protection Aarti'],
      bestFor: ['Mental confusion', 'Sudden problems', 'Foreign travel', 'Technology issues'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Confusion removal mantras', 'Hessonite energizing', 'Mental clarity rituals'],
      minAdvanceBooking: '3 days',
      cancellationPolicy: '48 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    },
    {
      id: 11,
      name: 'Ketu Shanti Pujan',
      icon: '🌘',
      image: '/images/navgrah/ketu.webp',
      description: 'South lunar node worship for spiritual liberation and karmic healing',
      price: '₹20,000',
      duration: '3-7 days*',
      rating: 4,
      bookingUrl: '/booking/navgrah/ketu-shanti',
      priests: 3,
      materials: ['Cat eye gemstone', 'Sesame seeds', 'White flowers', 'Silver items', 'Sacred ash'],
      includes: ['3 qualified priests', 'Spiritual materials', 'Ketu yantra', 'Karmic healing', 'Liberation prayers'],
      ritualSteps: ['Spiritual Ganesh Invocation', 'Ketu Kalash Setup', 'Ketu Mantra Jaap', 'Karmic Offerings', 'Liberation Mantras', 'Moksha Aarti'],
      bestFor: ['Spiritual growth', 'Karmic issues', 'Past life problems', 'Meditation enhancement'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Karmic healing mantras', 'Cat eye blessings', 'Spiritual liberation rituals'],
      minAdvanceBooking: '3 days',
      cancellationPolicy: '48 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    },
    {
      id: 12,
      name: 'Budh Shanti Pujan',
      icon: '🟢',
      image: '/images/navgrah/budh.webp',
      description: 'Mercury planet worship for communication skills and business success',
      price: '₹16,000',
      duration: '1-5 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/budh-shanti',
      priests: 2,
      materials: ['Emerald', 'Green moong dal', 'Green flowers', 'Bronze items', 'Mint leaves'],
      includes: ['2 skilled priests', 'Communication materials', 'Budh yantra', 'Speech blessings', 'Business success prayers'],
      ritualSteps: ['Ganesh Communication Puja', 'Budh Kalash Establishment', 'Mercury Mantras', 'Green Offerings', 'Communication Prayers', 'Success Aarti'],
      bestFor: ['Communication issues', 'Business growth', 'Students', 'Speech problems'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Communication enhancement', 'Emerald energizing', 'Business success mantras'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '24 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    },
    {
      id: 13,
      name: 'Shukra Shanti Pujan',
      icon: '🔵',
      image: '/images/navgrah/shukra.webp',
      description: 'Venus worship for love, beauty, wealth and artistic enhancement',
      price: '₹17,000',
      duration: '3-7 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/shukra-shanti',
      priests: 2,
      materials: ['Diamond/White zircon', 'White flowers', 'Rice', 'Silver items', 'Rose petals'],
      includes: ['2 experienced priests', 'Beauty materials', 'Shukra yantra', 'Love blessings', 'Wealth prayers'],
      ritualSteps: ['Beautiful Ganesh Puja', 'Shukra Kalash Decoration', 'Venus Mantras', 'White Flower Offerings', 'Beauty Prayers', 'Prosperity Aarti'],
      bestFor: ['Love relationships', 'Beauty enhancement', 'Artistic skills', 'Wealth attraction'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Love enhancement mantras', 'Diamond blessings', 'Beauty and wealth rituals'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '24 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    },
    {
      id: 14,
      name: 'Surya Shanti Pujan',
      icon: '☀️',
      image: '/images/navgrah/surya.webp',
      description: 'Sun worship for health, vitality, leadership and government favor',
      price: '₹19,000',
      duration: '3-7 days*',
      rating: 5,
      bookingUrl: '/booking/navgrah/surya-shanti',
      priests: 3,
      materials: ['Ruby', 'Red flowers', 'Wheat', 'Copper items', 'Jaggery'],
      includes: ['3 qualified priests', 'Solar materials', 'Surya yantra', 'Health blessings', 'Leadership prayers'],
      ritualSteps: ['Powerful Ganesh Puja', 'Surya Kalash Installation', 'Sun Mantras', 'Red Offerings', 'Health Prayers', 'Vitality Aarti'],
      bestFor: ['Health issues', 'Government jobs', 'Leadership roles', 'Father-related problems'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Health enhancement mantras', 'Ruby energizing', 'Leadership development rituals'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '24 hours before ceremony',
      platformSitePreferred: true,
      siteOnly: true,
      platformSiteMessage: 'Navgrah pujan is more effective when performed at our sacred temple site with proper planetary alignments and consecrated yantras.'
    }
  ],
  'vishist-sanskar': [
    {
      id: 15,
      name: 'Naam Karan Sanskar',
      icon: '👶',
      image: '/images/sanskar/naam_karan.webp',
      description: 'Traditional Hindu naming ceremony for newborns with astrological guidance',
      price: '₹12,000',
      duration: '3-5 hours*',
      rating: 5,
      bookingUrl: '/booking/sanskar/naam-karan',
      priests: 2,
      materials: ['Sacred thread', 'Honey', 'Ghee', 'Rice', 'Flowers', 'Gold ornament'],
      includes: ['2 experienced priests', 'Astrological name consultation', 'Sacred ceremony', 'Name scroll', 'Blessed tilak'],
      ritualSteps: ['Ganesh Puja', 'Jyotish consultation', 'Name selection', 'Sacred chanting', 'Honey ceremony', 'Blessing ritual'],
      bestFor: ['Newborn blessings', 'Astrological naming', 'Family harmony', 'Child protection'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Astrological name guidance', 'Sacred name scroll', 'Family participation'],
      minAdvanceBooking: '3 days',
      cancellationPolicy: '24 hours before ceremony'
    },
    {
      id: 16,
      name: 'Annaprashan Sanskar',
      icon: '🍚',
      image: '/images/sanskar/annaprashan.webp',
      description: 'First food feeding ceremony for babies with sacred blessings',
      price: '₹10,000',
      duration: '3 hours*',
      rating: 5,
      bookingUrl: '/booking/sanskar/annaprashan',
      priests: 1,
      materials: ['Sacred rice', 'Ghee', 'Honey', 'Fruits', 'Silver bowl', 'Sacred water'],
      includes: ['Experienced priest', 'Sacred food preparation', 'Blessing ceremony', 'Silver spoon blessing', 'Health prayers'],
      ritualSteps: ['Ganesh invocation', 'Food sanctification', 'First feeding ritual', 'Health mantras', 'Family blessings'],
      bestFor: ['Baby health', 'Digestive blessings', 'Growth enhancement', 'Nutritional start'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Sacred food blessing', 'Health enhancement mantras', 'Family celebration'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '12 hours before ceremony'
    },
    {
      id: 17,
      name: 'Upnayan Sanskar',
      icon: '📿',
      image: '/images/sanskar/upnayan.webp',
      description: 'Sacred thread ceremony marking spiritual initiation and education beginning',
      price: '₹25,000',
      duration: '1-2 days*',
      rating: 5,
      bookingUrl: '/booking/sanskar/upnayan',
      priests: 3,
      materials: ['Sacred thread', 'Yajnopavit', 'Wooden stick', 'Sacred fire', 'Vedic texts', 'Offerings'],
      includes: ['3 learned priests', 'Complete ceremony materials', 'Sacred thread blessing', 'Vedic initiation', 'Educational blessings'],
      ritualSteps: ['Elaborate Ganesh Puja', 'Fire ceremony', 'Sacred thread wearing', 'Gayatri mantra initiation', 'Guru-shishya ceremony', 'Educational prayers'],
      bestFor: ['Boys education start', 'Spiritual initiation', 'Cultural tradition', 'Academic success'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Traditional Vedic ceremony', 'Sacred thread sanctification', 'Educational blessing rituals'],
      minAdvanceBooking: '5 days',
      cancellationPolicy: '72 hours before ceremony'
    },
    {
      id: 18,
      name: 'Shubh Vivah',
      icon: '💒',
      image: '/images/sanskar/vivah.webp',
      description: 'Complete Hindu wedding ceremony with all traditional rituals and blessings',
      price: '₹75,000',
      duration: '4-6 hours*',
      rating: 5,
      bookingUrl: '/booking/sanskar/shubh-vivah',
      priests: 5,
      materials: ['Wedding fire altar', 'Sacred threads', 'Vermillion', 'Mangalsutra', 'Wedding garlands', 'Rice', 'Flowers'],
      includes: ['5 experienced priests', 'Complete wedding setup', 'All ceremonial materials', 'Photography coordination', 'Wedding certificate'],
      ritualSteps: ['Ganesh Puja', 'Mandap setup', 'Kanyadaan ceremony', 'Agni Parinay', 'Saat Pheras', 'Sindoor ceremony', 'Ashirwad'],
      bestFor: ['Traditional Hindu wedding', 'Cultural ceremonies', 'Family blessings', 'Marital harmony'],
      languages: ['Sanskrit', 'Hindi', 'English', 'Regional languages'],
      specialFeatures: ['Complete traditional ceremony', 'Multiple priest coordination', 'Cultural authenticity', 'Family involvement'],
      minAdvanceBooking: '15 days',
      cancellationPolicy: '7 days before ceremony'
    }
  ],
  'shanti-samridhi': [
    {
      id: 19,
      name: 'Grih Pravesh Pujan',
      icon: '🏠',
      image: '/images/shanti_samridhi/grih_pravesh.webp',
      description: 'Sacred home entry ceremony to purify new homes and invoke divine blessings for prosperity',
      price: '₹18,000',
      duration: '5-7 hours*',
      rating: 5,
      bookingUrl: '/booking/shanti-samridhi/grih-pravesh',
      priests: 3,
      materials: ['Kalash', 'Coconut', 'Mango leaves', 'Sacred water', 'Rice', 'Turmeric', 'Incense', 'Flowers'],
      includes: ['3 experienced priests', 'Complete purification ceremony', 'Vastu correction rituals', 'Home blessing', 'Sacred tilak'],
      ritualSteps: ['Ganesh Puja', 'Vastu Shanti', 'Kalash Sthapana', 'Home purification', 'Deity installation', 'Family blessings'],
      bestFor: ['New home entry', 'Vastu correction', 'Family protection', 'Prosperity enhancement'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Vastu harmony rituals', 'Home purification ceremonies', 'Prosperity blessings'],
      minAdvanceBooking: '3 days',
      cancellationPolicy: '24 hours before ceremony'
    },
    {
      id: 20,
      name: 'Satyanarayan Pujan',
      icon: '🪷',
      image: '/images/shanti_samridhi/satyanarayan.webp',
      description: 'Divine worship of Lord Vishnu for truth, prosperity, and fulfillment of wishes',
      price: '₹12,000',
      duration: '2-3 hours*',
      rating: 5,
      bookingUrl: '/booking/shanti-samridhi/satyanarayan',
      priests: 2,
      materials: ['Banana leaves', 'Panchamrit', 'Flowers', 'Fruits', 'Sweets', 'Sacred thread', 'Incense'],
      includes: ['2 qualified priests', 'Complete puja materials', 'Satyanarayan Katha', 'Prasadam preparation', 'Family participation'],
      ritualSteps: ['Ganesh Invocation', 'Kalash Setup', 'Satyanarayan Mantras', 'Katha Recitation', 'Aarti', 'Prasadam Distribution'],
      bestFor: ['Wish fulfillment', 'Family harmony', 'Business success', 'Removing obstacles'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Traditional Katha recitation', 'Wish fulfillment prayers', 'Family blessing ceremony'],
      minAdvanceBooking: '2 days',
      cancellationPolicy: '12 hours before ceremony'
    },
    {
      id: 21,
      name: 'Shanti Path',
      icon: '🕉️',
      image: '/images/shanti_samridhi/shanti_path.webp',
      description: 'Sacred peace chanting ceremony for mental tranquility, spiritual harmony and cosmic peace',
      price: '₹10,000',
      duration: '2-3 hours*',
      rating: 5,
      bookingUrl: '/booking/shanti-samridhi/shanti-path',
      priests: 2,
      materials: ['Sacred books', 'Incense', 'Diya', 'Flowers', 'Sacred water', 'White cloth'],
      includes: ['2 learned priests', 'Peace chanting ceremony', 'Meditation guidance', 'Spiritual counseling', 'Blessed vibhuti'],
      ritualSteps: ['Opening prayers', 'Shanti Mantras', 'Peace meditation', 'Cosmic harmony chanting', 'Final blessings'],
      bestFor: ['Mental peace', 'Stress relief', 'Spiritual growth', 'Family harmony'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Peace meditation guidance', 'Stress relief mantras', 'Spiritual counseling'],
      minAdvanceBooking: '1 day',
      cancellationPolicy: '6 hours before ceremony'
    }
  ],
  'dosh-nivaran': [
    {
      id: 22,
      name: 'Manglik Dosh Nivaran',
      icon: '🔴',
      image: '/images/dosh_nivaran/mangal_dosh.webp',
      description: 'Powerful remedy ceremony to neutralize Manglik dosha effects and ensure harmonious marriage prospects',
      price: '₹25,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/dosh-nivaran/manglik-dosh',
      priests: 3,
      materials: ['Red coral', 'Mangal yantra', 'Red flowers', 'Copper items', 'Sacred fire', 'Red cloth', 'Jaggery'],
      includes: ['3 qualified priests', 'Complete dosh nivaran ceremony', 'Mangal yantra energizing', 'Remedial mantras', 'Marriage harmony blessings'],
      ritualSteps: ['Ganesh Puja', 'Mangal Kalash Sthapana', 'Manglik Dosh Nivaran Mantras', 'Havan ceremony', 'Yantra installation', 'Final blessings'],
      bestFor: ['Manglik individuals', 'Marriage delays', 'Marital harmony', 'Removing marriage obstacles'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Manglik dosha analysis', 'Remedial fire ceremony', 'Marriage obstacle removal', 'Yantra energizing'],
      minAdvanceBooking: '5 days',
      platformSitePreferred: true,
      siteOnly: true,
      cancellationPolicy: '48 hours before ceremony'
    },
    {
      id: 23,
      name: 'Kaal Sarp Dosh Nivaran',
      icon: '🐍',
      image: '/images/dosh_nivaran/kaal_sarp.webp',
      description: 'Sacred ritual to neutralize Kaal Sarp Yoga effects and remove obstacles caused by serpent planetary alignment',
      price: '₹30,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/dosh-nivaran/kaal-sarp-dosh',
      priests: 4,
      materials: ['Silver snake idols', 'Nag yantra', 'Black sesame', 'Milk', 'Sacred fire', 'Blue flowers', 'Rahu-Ketu remedial items'],
      includes: ['4 experienced priests', 'Complete Kaal Sarp dosh ceremony', 'Nag yantra installation', 'Serpent pacification rituals', 'Obstacle removal prayers'],
      ritualSteps: ['Elaborate Ganesh Puja', 'Nag Kalash Puja', 'Rahu-Ketu Shanti', 'Kaal Sarp Nivaran Havan', 'Serpent deity worship', 'Liberation mantras'],
      bestFor: ['Kaal Sarp Yoga individuals', 'Removing life obstacles', 'Career advancement', 'Mental peace'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Complete astrological remedy', 'Serpent deity worship', 'Obstacle removal ceremony', 'Life progress enhancement'],
      minAdvanceBooking: '7 days',
      platformSitePreferred: true,
      siteOnly: true,
      cancellationPolicy: '72 hours before ceremony'
    },
    {
      id: 31,
      name: 'Pitra Dosh Nivaran',
      icon: '👥',
      image: '/images/dosh_nivaran/pitra_dosh.webp',
      description: 'Sacred ancestral dosh removal ceremony to pacify departed souls and remove family curses',
      price: '₹28,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/dosh-nivaran/pitra-dosh',
      priests: 4,
      materials: ['Black sesame seeds', 'Kusha grass', 'Sacred water', 'Pind materials', 'Silver coins', 'White flowers', 'Ancestral offerings'],
      includes: ['4 experienced priests', 'Complete Pitra Dosh ceremony', 'Ancestral pacification rituals', 'Family curse removal', 'Spiritual healing'],
      ritualSteps: ['Ganesh Puja', 'Pitra invocation', 'Ancestral offerings', 'Dosh nivaran mantras', 'Pitru shanti havan', 'Liberation prayers'],
      bestFor: ['Family problems', 'Ancestral curses', 'Delayed marriage', 'Financial obstacles'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Ancestral soul pacification', 'Family curse removal', 'Generational healing', 'Spiritual liberation'],
      minAdvanceBooking: '5 days',
      platformSitePreferred: true,
      siteOnly: true,
      cancellationPolicy: '48 hours before ceremony'
    },
    {
      id: 32,
      name: 'Mool Dosh Nivaran',
      icon: '🌿',
      image: '/images/dosh_nivaran/mool_shanti.webp',
      description: 'Sacred Vedic ritual to pacify the malefic effects of Mool Nakshatra dosha and protect newborns and family members from its adverse influences',
      price: '₹35,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/dosh-nivaran/mool-dosh-nivaran',
      priests: 3,
      materials: ['Sacred herbs', 'Navagraha yantra', 'White flowers', 'Ghee', 'Sacred fire', 'Turmeric', 'Kusha grass', 'Coconut'],
      includes: ['3 qualified priests', 'Complete Mool Dosh Nivaran ceremony', 'Nakshatra shanti rituals', 'Protective mantras recitation', 'Blessed prasadam'],
      ritualSteps: ['Ganesh Puja', 'Nakshatra invocation', 'Mool Dosh Nivaran Mantras', 'Havan ceremony', 'Shanti offerings', 'Final protection blessings'],
      bestFor: ['Mool Nakshatra born individuals', 'Newborn protection', 'Family safety', 'Removing birth dosha effects'],
      languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: ['Nakshatra-specific remedy', 'Newborn protection ceremony', 'Complete dosha pacification', 'Family well-being rituals'],
      minAdvanceBooking: '3 days',
      platformSitePreferred: true,
      cancellationPolicy: '48 hours before ceremony'
    }
  ],
  'shradh-karm': [
    {
      id: 24,
      name: 'Gaya Ji Shradhh Karm',
      icon: '🪔',
      image: '/images/shradh_karm/gayaji.webp',
      description: 'Sacred Shradh ceremony performed at holy Gaya for ancestral salvation and spiritual liberation',
      price: '₹35,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/shradh-karm/gaya-shradh',
      priests: 4,
      materials: ['Sacred rice', 'Sesame seeds', 'Kusha grass', 'Holy water', 'Silver coins', 'Sacred thread', 'Pind offerings'],
      includes: ['4 experienced priests', 'Complete Gaya Shradh ceremony', 'Pind daan rituals', 'Ancestral blessings', 'Spiritual guidance'],
      ritualSteps: ['Sankalp ceremony', 'Pitra invocation', 'Pind daan ritual', 'Brahmin feeding', 'Ancestral prayers', 'Liberation mantras'],
      bestFor: ['Ancestral peace', 'Pitru dosha removal', 'Spiritual liberation', 'Family harmony'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Sacred Gaya tradition', 'Pitru moksha ceremony', 'Complete ancestral rites', 'Spiritual liberation'],
      minAdvanceBooking: '10 days',
      platformSitePreferred: true,
      cancellationPolicy: '5 days before ceremony',
      siteOnly: true,
      siteOnlyMessage: 'Gaya Ji Shradhh Karm is a sacred ritual that can only be performed at Gaya, to ensure the authenticity and spiritual potency of the ceremony.'
    },
    {
      id: 25,
      name: 'Kashi Shradh Karm',
      icon: '🕉️',
      image: '/images/shradh_karm/kashi.jpg',
      description: 'Holy Shradh ceremony in sacred Kashi (Varanasi) for ancestral blessings and moksha',
      price: '₹30,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/shradh-karm/kashi-shradh',
      priests: 3,
      materials: ['Ganga water', 'Sacred flowers', 'Pind materials', 'Kusha grass', 'Sacred fire', 'Offerings'],
      includes: ['3 qualified priests', 'Kashi Shradh ceremony', 'Ganga aarti', 'Ancestral offerings', 'Moksha prayers'],
      ritualSteps: ['Ganga invocation', 'Pitru sankalp', 'Shradh ceremony', 'Pind offerings', 'Ganga aarti', 'Final blessings'],
      bestFor: ['Ancestral moksha', 'Spiritual purification', 'Divine blessings', 'Pitru satisfaction'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Sacred Kashi rituals', 'Ganga blessings', 'Moksha ceremonies', 'Ancient traditions'],
      minAdvanceBooking: '7 days',
      platformSitePreferred: true,
      cancellationPolicy: '3 days before ceremony',
      siteOnly: true,
      siteOnlyMessage: 'Kashi Shradh Karm is a sacred ritual that can only be performed at Kashi, to ensure the authenticity and spiritual potency of the ceremony.'
    },
    {
      id: 26,
      name: 'Tripindi Shradh',
      icon: '🕯️',
      image: '/images/shradh_karm/tripindi.jpg',
      description: 'Three-generation ancestral Shradh ceremony for complete family lineage spiritual peace',
      price: '₹25,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/shradh-karm/tripindi-shradh',
      priests: 3,
      materials: ['Three sets of offerings', 'Sacred rice', 'Sesame seeds', 'Holy water', 'Flowers', 'Pind materials'],
      includes: ['3 experienced priests', 'Three-generation ceremony', 'Complete ancestral rites', 'Family blessings', 'Pitru peace'],
      ritualSteps: ['Triple sankalp', 'Three-generation invocation', 'Tripindi offerings', 'Ancestral feeding', 'Liberation prayers', 'Final aarti'],
      bestFor: ['Three-generation peace', 'Family lineage blessings', 'Ancestral harmony', 'Pitru dosha removal'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Three-generation ceremony', 'Complete lineage rites', 'Family peace rituals', 'Ancestral satisfaction'],
      minAdvanceBooking: '5 days',
      platformSitePreferred: true,
      cancellationPolicy: '48 hours before ceremony',
      siteOnly: true,
      siteOnlyMessage: 'Tripindi Shradh is a sacred ritual that can only be performed at our site, to ensure the authenticity and spiritual potency of the ceremony.'
    },
    {
      id: 27,
      name: 'Narayan Bali',
      icon: '🔥',
      image: '/images/shradh_karm/narayan_bali.jpg',
      description: 'Powerful ceremony to pacify restless souls and remove ancestral curses through Narayan Bali ritual',
      price: '₹40,000',
      duration: '1 day*',
      rating: 5,
      bookingUrl: '/booking/shradh-karm/narayan-bali',
      priests: 5,
      materials: ['Sacred fire', 'Narayan idol', 'Special offerings', 'Ghee', 'Sacred herbs', 'Brahmin feeding materials'],
      includes: ['5 learned priests', 'Complete Narayan Bali ceremony', 'Fire rituals', 'Soul pacification', 'Curse removal prayers'],
      ritualSteps: ['Elaborate setup', 'Narayan installation', 'Fire ceremony', 'Soul invocation', 'Pacification rituals', 'Final liberation'],
      bestFor: ['Restless soul peace', 'Ancestral curse removal', 'Supernatural problems', 'Family protection'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Soul pacification', 'Curse removal ceremony', 'Supernatural protection', 'Complete liberation rites'],
      minAdvanceBooking: '15 days',
      platformSitePreferred: true,
      cancellationPolicy: '7 days before ceremony',
      siteOnly: true,
      siteOnlyMessage: 'Narayan Bali is a sacred ritual that can only be performed at our site, to ensure the authenticity and spiritual potency of the ceremony.'
    }
  ],
  'ramayan-sunder-kand': [
    {
      id: 28,
      name: 'Akhand Ramcharitmanas',
      icon: '📖',
      image: '/images/ramayan/ramayan_path.jpg',
      description: 'Complete uninterrupted recitation of Ramcharitmanas for divine blessings and spiritual purification',
      price: '₹25,000',
      duration: '30 hours*',
      rating: 5,
      bookingUrl: '/booking/ramayan/akhand-ramcharitmanas',
      priests: 6,
      materials: ['Sacred books', 'Continuous diya', 'Flowers', 'Prasadam', 'Sacred water', 'Incense'],
      includes: ['6 dedicated priests', '48-hour continuous recitation', 'Sacred setup', 'Daily prasadam', 'Completion ceremony'],
      ritualSteps: ['Sacred setup', 'Continuous recitation', 'Daily aarti', 'Prasadam distribution', 'Final prayers', 'Blessing ceremony'],
      bestFor: ['Major wishes fulfillment', 'Family harmony', 'Spiritual purification', 'Divine protection'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['48-hour continuous recitation', 'Multiple priest coordination', 'Sacred atmosphere maintenance', 'Complete spiritual immersion'],
      minAdvanceBooking: '10 days',
      platformSitePreferred: true,
      cancellationPolicy: '5 days before ceremony'
    },
    {
      id: 29,
      name: 'Sunder Kand Path',
      icon: '🌺',
      image: '/images/ramayan/sunder_kand.jpg',
      description: 'Sacred recitation of Sunder Kand for courage, strength and removal of obstacles through Hanuman blessings',
      price: '₹12,000',
      duration: '3-4 hours*',
      rating: 5,
      bookingUrl: '/booking/ramayan/sunder-kand',
      priests: 2,
      materials: ['Ramcharitmanas', 'Red flowers', 'Sindoor', 'Laddoos', 'Incense', 'Diya'],
      includes: ['2 experienced priests', 'Complete Sunder Kand recitation', 'Hanuman prayers', 'Blessed prasadam', 'Courage blessings'],
      ritualSteps: ['Hanuman invocation', 'Sunder Kand recitation', 'Hanuman aarti', 'Strength prayers', 'Prasadam distribution'],
      bestFor: ['Overcoming obstacles', 'Gaining courage', 'Job success', 'Protection from enemies'],
      languages: ['Sanskrit', 'Hindi'],
      specialFeatures: ['Complete Sunder Kand recitation', 'Hanuman blessings', 'Obstacle removal prayers', 'Courage enhancement'],
      minAdvanceBooking: '2 days',
      platformSitePreferred: true,
      cancellationPolicy: '24 hours before ceremony'
    },
    {
      id: 30,
      name: 'Hanuman Chalisa Path',
      icon: '🌸',
      image: '/images/ramayan/hanuman_path.jpg',
      description: 'Powerful recitation of Hanuman Chalisa for protection, strength and divine intervention in difficulties',
      price: '₹8,000',
      duration: '2-3 hours*',
      rating: 5,
      bookingUrl: '/booking/ramayan/hanuman-chalisa',
      priests: 1,
      materials: ['Hanuman Chalisa book', 'Red flowers', 'Sindoor', 'Oil lamp', 'Sweets', 'Red cloth'],
      includes: ['Experienced priest', 'Multiple Chalisa recitations', 'Hanuman blessings', 'Protection prayers', 'Sweet prasadam'],
      ritualSteps: ['Hanuman invocation', 'Chalisa recitation (108 times)', 'Hanuman aarti', 'Protection mantras', 'Final blessings'],
      bestFor: ['Daily protection', 'Strength building', 'Fear removal', 'Quick problem solving'],
      languages: ['Hindi', 'Sanskrit'],
      specialFeatures: ['108 times recitation', 'Protection mantras', 'Fear removal prayers', 'Daily strength building'],
      minAdvanceBooking: '1 day',
      platformSitePreferred: true,
      cancellationPolicy: '6 hours before ceremony'
    }
  ]
};

// Customer Reviews Data
export const customerReviews: Review[] = [
  {
    id: 1,
    customerName: 'Rajesh Kumar',
    rating: 5,
    comment: 'The Maha Mrityunjaya Pujan was performed with utmost devotion. Felt truly blessed.',
    date: '2024-01-15',
    avatar: '/images/avatars/customer-1.jpg'
  },
  {
    id: 2,
    customerName: 'Priya Sharma',
    rating: 4,
    comment: 'Beautiful Rudrabhishekh ceremony. The priest was knowledgeable and sincere.',
    date: '2024-01-10',
    avatar: '/images/avatars/customer-2.jpg'
  },
  {
    id: 3,
    customerName: 'Amit Patel',
    rating: 5,
    comment: 'Peaceful spiritual experience. All rituals were performed authentically.',
    date: '2024-01-08',
    avatar: '/images/avatars/customer-3.jpg'
  }
];

// Spiritual Testimonials Data
export const spiritualTestimonials: Testimonial[] = [
  {
    id: 1,
    customerName: 'Sneha Reddy',
    photo: '/images/testimonials/customer-1.jpg',
    testimonial: 'The Maha Mrityunjaya Pujan brought peace to our family. Highly recommend.',
    service: 'Maha Mrityunjaya Pujan'
  },
  {
    id: 2,
    customerName: 'Vikram Singh',
    photo: '/images/testimonials/customer-2.jpg',
    testimonial: 'Authentic Rudrabhishekh ceremony. Very spiritual and meaningful experience.',
    service: 'Rudrabhishekh'
  }
];

// Featured Puja Services
export const featuredPujaServices: RecommendedService[] = [
  {
    id: 1,
    name: 'Maha Mrityunjaya Pujan Special',
    image: '/images/puja_service.jpg',
    price: '₹25,000',
    rating: 5,
    trending: false
  },
  {
    id: 2,
    name: 'Rudrabhishekh Premium',
    image: '/images/puja_samgri.jpg',
    price: '₹15,000',
    rating: 4
  }
];

// Spiritual Promotions
export const spiritualPromotions: Promotion[] = [
  {
    id: 1,
    title: '20% Off Maha Mrityunjaya Pujan',
    description: 'Get 20% discount on Maha Mrityunjaya Pujan services',
    discount: '20%',
    validUntil: '2024-02-29',
    code: 'BLESSED20'
  },
  {
    id: 2,
    title: 'Free Prasadam with Rudrabhishekh',
    description: 'Complimentary blessed prasadam with any Rudrabhishekh ceremony',
    discount: 'Free',
    validUntil: '2024-03-15',
    code: 'PRASADAM'
  }
];

// Service Benefits Data
export const serviceBenefits: Record<string, ServiceBenefit[]> = {
  'maha-mrityunjaya-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Maha Mrityunjaya Poojan is a powerful Vedic ritual dedicated to Lord Shiva, performed to seek health, longevity, protection, and inner strength during challenging phases of life. It centres around the chanting of the Maha Mrityunjaya Mantra with proper sankalp and offerings to invoke divine blessings.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: ' This pooja is performed to overcome illness, fear, emotional stress, and recurring difficulties, and to invoke Lord Shiva’s healing and protective energies.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing health concerns,| Families seeking protection and overall well-being | People experiencing emotional or mental stress',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Mondays, Pradosh Kaal, Shravan Month(Can be performed anytime during medical or emotional emergencies)',
      icon: '🙏'
    }
  ],
  'rudrabhishekh': [
    // {
    //   id: 1,
    //   title: 'What is this Pujan?',
    //   description: 'Rudrabhishek Poojan is a sacred Shaiv ritual in which the Shivling is bathed (abhishek) with holy substances such as water, milk, curd, honey, ghee, and sacred herbal liquids. The abhishek is performed alongside the recitation of Rudra Ashtadhyayi Paath, a revered Vedic text dedicated to Lord Rudra, along with prescribed Vedic mantras.',
    //   icon: '🛡️'
    // },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: ' This pooja is performed to invoke peace, prosperity, emotional balance, and spiritual upliftment. Rudrabhishek is especially known for calming turbulent energies, reducing mental unrest, and strengthening one’s connection with Lord Shiva.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Devotees of Lord Shiva,| Families seeking harmony and positivity | Individuals beginning a new phase of life | Those seeking inner calm and spiritual grounding',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Mondays, Shravan Month, Maha Shivratri, Pradosh Vrat (Can also be performed on other suitable days based on intent)',
      icon: '🙏'
    }
  ],
  'maha-rudrai-anushthaan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Maha Rudraaii Anushthan is an advanced Shaiv anushthan involving the disciplined and repeated recitation of Rudra Ashtadhyayi Paath over an extended period, performed with strict continuity, mantra discipline, and sankalp. This anushthan is undertaken to invoke the highest grace of Lord Rudra for transformation, protection, and deep spiritual alignment.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This anushthan is performed to address long-standing obstacles, intense life challenges, karmic blockages, and to seek profound inner stability. It is also undertaken as a form of serious Shiva sadhana during critical spiritual or life transitions.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing prolonged or unresolved challenges,| Families undergoing major transitions or instability | Serious Shiva devotees inclined towards disciplined sadhana | Those seeking deep spiritual grounding and transformation',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Shravan Month, Maha Shivratri, Pradosh Vrat, or as advised by the Acharya (Anushthan is always aligned to an appropriate muhurat and duration)',
      icon: '🙏'
    }
  ],

 'durga-saptshati-path': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Durga Saptshati Paath is the sacred recitation of the Devi Mahatmya, comprising 700 verses that glorify Goddess Durga in her various forms. The paath invokes strength, protection, courage, and balance through disciplined chanting and sankalp.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This path is performed to overcome fear, emotional instability, repeated challenges, and periods of stagnation, and to restore inner strength and protective energy.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals seeking emotional balance and courage | Families facing repeated challenges or instability | Devotees of Goddess Durga | Those undergoing important life transitions ',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Navratri, Fridays, Ashtami, Navami',
      icon: '🙏'
    }
  ],
  'baglamukhi-path': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Baglamukhi Poojan is a focused Devi ritual invoking Goddess Baglamukhi, one of the Mahavidyas, known for her power to stambhan (stilling) negativity, opposition, confusion, and hostile influences. The pooja is performed through disciplined mantra path, sankalp, and offerings under strict ritual restraint.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pooja is performed to reduce opposition, calm mental turbulence, control adverse situations, and restore clarity and stability. It is often recommended during periods of conflict, legal stress, professional resistance, or intense mental disturbance.Beyond addressing opposition, Baglamukhi Poojan is also performed to strengthen inner resolve, sharpen decision-making, and cultivate steadiness during important phases of life. It supports clarity, discipline, and mental composure, especially when focus and restraint are required.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing strong opposition or conflict,| Those experiencing legal, professional, or interpersonal challenges | People dealing with mental unrest, confusion, or instability | Devotees advised by an Acharya for Baglamukhi upasana ',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Tuesdays, Thursdays, or as advised by the Acharya',
      icon: '🙏'
    }
  ],
  
  
  'sat-chandi-yagya': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shat Chandi Yagya is a highly intensive Devi anushthan involving 100 complete recitations of Durga Saptashati, followed by a Chandi Homa. It is one of the most powerful Shakti rituals and demands continuity, discipline, and collective sadhana under senior Acharya supervision.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This anushthan is undertaken during severe or long-standing life challenges, existential crises, or when repeated remedies have not yielded results. It is meant to invoke the supreme protective and transformative power of Goddess Durga.| In addition to addressing major challenges, Shat Chandi Yagya is also undertaken as a powerful Shakti sadhana for protection, spiritual strengthening, and alignment during decisive life phases. Many families perform it to establish long-term stability, resilience, and divine safeguarding. |Shat Chandi Yagya is not only a remedial ritual but also a protective and strengthening anushthan. When performed with sincerity and discipline, it is believed to create a strong spiritual foundation that supports stability, confidence, and continuity over time.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing prolonged or critical obstacles,| Families dealing with deep-rooted instability | Those advised by experienced Acharyas | Devotees prepared for disciplined, multi-day anushthan',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Determined strictly through Acharya consultation and muhurat alignment',
      icon: '🙏'
    },

    {
      id: 5,
      title: '🪔 Nature of this Anushthan',
      description: 'Multi-day ritual (typically 7–9 days) |Conducted by a team of Kashi-trained Acharyas | Strict daily path discipline | Completion with Chandi Homa and Shanti Paath',
      icon: '🙏'
    }
  ],

  // Add serviceBenefits for other services that don't have them yet
  'shani-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shani Shanti Pujan is a powerful planetary ritual dedicated to Lord Shani (Saturn) to reduce the malefic effects of Saturn and bring stability, discipline, and gradual progress in life.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to overcome career obstacles, financial problems, legal issues, delays in marriage, and other challenges caused by the negative influence of Saturn.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing Saturn Mahadasha or Antardasha | People with career obstacles | Those experiencing financial problems | Individuals with delayed marriage',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Saturdays, during Saturn transits, or as advised by astrologer',
      icon: '🙏'
    }
  ],

  'mangal-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Mangal Shanti Pujan is performed to pacify planet Mars and reduce the effects of Manglik dosha, enhancing courage, strength, and removing obstacles in marriage.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to neutralize Manglik dosha, remove delays in marriage, enhance courage and confidence, and resolve property-related issues.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Manglik individuals | Those facing marriage delays | People lacking courage or confidence | Individuals with property disputes',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Tuesdays, during Mars transits, or before marriage',
      icon: '🙏'
    }
  ],
  'guru-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Guru Shanti Pujan is performed to strengthen the positive influence of Jupiter (Guru) for wisdom, knowledge, spiritual growth, and educational success.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to enhance educational success, gain wisdom, improve teacher-student relationships, and achieve career advancement.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Students and scholars | Those seeking career advancement | People wanting spiritual growth | Individuals with weak Jupiter in horoscope',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Thursdays, during Jupiter transits, or before important exams',
      icon: '🙏'
    }
  ],
  'rahu-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Rahu Shanti Pujan is performed to pacify the malefic effects of Rahu, promoting mental clarity, protection from negative influences, and overall well-being.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to reduce the negative effects of Rahu, improve mental clarity, protect from evil influences, and enhance overall well-being.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals affected by Rahu dosha | Those experiencing confusion or mental stress | People seeking protection from negative influences | Individuals with Rahu-related astrological issues',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Graha-specific weekdays, Amavasya, Purnima, or astrologically recommended muhurats.',
      icon: '🙏'
    }
  ],

  'ketu-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Ketu Shanti Pujan is performed to pacify the malefic effects of Ketu, promoting spiritual growth, mental peace, and overall well-being.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to promote spiritual growth, mental peace, and overall well-being.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals affected by Ketu dosha | Those seeking spiritual growth | People wanting mental peace | Individuals with Ketu-related astrological issues',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Graha-specific weekdays, Amavasya, Purnima, or astrologically recommended muhurats.',
      icon: '🙏'
    }
  ],

  'budh-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Budh Shanti Pujan is performed to strengthen the positive influence of Mercury (Budh) for communication, intellect, and business success.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'Navagrah Poojan is performed to support balance during challenging planetary periods (dasha, antardasha, or transits), to strengthen favourable grahas, and to reduce the intensity of unfavourable influences—without fear or superstition.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals advised after proper kundali analysis | Those experiencing repeated delays or instability | People entering major life transitions (career, marriage, relocation) | Individuals with weak Mercury in horoscope',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Graha-specific weekdays, Amavasya, Purnima, or astrologically recommended muhurats.',
      icon: '🙏'
    }
  ],

  'shukra-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shukra Shanti Pujan is performed to strengthen the positive influence of Venus (Shukra) for love, relationships, creativity, and material comforts.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to promote love, enhance relationships, boost creativity, and improve material comforts.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals advised after proper kundali analysis | Those experiencing repeated delays or instability | People entering major life transitions (career, marriage, relocation) | Individuals with weak Venus in horoscope',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Graha-specific weekdays, Amavasya, Purnima, or astrologically recommended muhurats.',
      icon: '🙏'
    }
  ],

  'surya-shanti-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Surya Shanti Pujan is performed to strengthen the positive influence of Sun (Surya) for vitality, leadership, confidence, and overall well-being.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'Navagrah Poojan is performed to support balance during challenging planetary periods (dasha, antardasha, or transits), to strengthen favourable grahas, and to reduce the intensity of unfavourable influences—without fear or superstition.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals advised after proper kundali analysis | Those experiencing repeated delays or instability | People entering major life transitions (career, marriage, relocation) | Individuals with weak Sun in horoscope',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Graha-specific weekdays, Amavasya, Purnima, or astrologically recommended muhurats.',
      icon: '🙏'
    }
  ],

  'naam-karan-sanskar': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Naam Karan Sanskar is the Vedic ceremony in which a newborn child is formally given a name through mantra, sankalp, and Agni sakshi. The name is sanctified in alignment with family lineage, tradition, and auspicious intent.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To bless the child with a positive identity, good health, stability, and a harmonious life path, and to formally welcome the child into the family’s dharmic fold.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Parents of newborn children | Families wishing to follow traditional naming rituals ',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: '11th or 12th day after birth, or an astrologically determined muhurat' ,
      icon: '🙏'
    }
  ],
  'annaprashan-sanskar': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Annaprashan Sanskar marks a child’s first intake of solid food, symbolising nourishment, growth, and readiness for the next stage of life. The ceremony involves mantra chanting, sankalp, and offerings to invoke blessings for health and prosperity. ' ,
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To bless the child with good digestion, health, steady growth, and abundance .',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Parents of infants | Families wishing to follow traditional rites for child nourishment',

      icon: '✨'
    },
    
    {
      id: 4,
      title: 'Who should consider this?',
      description: 'Parents of infants aged 6 – 8 months | Families wishing to follow traditional rites for child nourishment ',
      icon: '🙏'
    },
    {
      id: 5,
      title: 'Auspicious Timing',
      description: '6th or 8th month after birth, or an astrologically suitable muhurat',
      icon: '🙏'
    }
  ],
  'upnayan-sanskar': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Upanayan Sanskar is the sacred thread ceremony that initiates the child into Vedic learning, discipline, and dharmic responsibility.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To formally guide the child towards education, self-discipline, and spiritual awareness..',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families following Vedic traditions | Children entering formal education | Those wishing to uphold dharmic values',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Astrologically determined muhurat usually done at 12 years of age',
      icon: '🙏'
    }
  ],
  'shubh-vivah': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shubh Vivaah is the complete Vedic marriage ceremony (Vivaha Sanskar) performed as per shastric vidhi, uniting two individuals in the presence of Agni, Devatas, and elders. It involves mantra chanting, sankalp, and various rituals to sanctify the union and invoke blessings for a harmonious married life. ' ,
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To sanctify marriage with blessings for harmony, stability, prosperity, and shared dharma.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Couples opting for traditional Vedic weddings | Families prioritising ritual authenticity | People wishing for strong marital foundations.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Astrologically determined muhurat',
      icon: '🙏'
    }
  ],
  'grih-pravesh-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Grih Pravesh Poojan is a purification and invocation ritual performed before entering a new home. It is designed to cleanse the space, balance energies, and invite peace, prosperity, and protection for all residents.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To ensure harmony, health, and positive energy in a new or renovated living space.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families moving to new homes | New property owners | Those seeking Vastu correction | Individuals wanting divine protection for their home',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'After consulting astrologer for auspicious date and time (muhurat)',
      icon: '🙏'
    }
  ],

  'satyanarayan-pujan': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Satyanarayan Katha is a devotional ritual dedicated to Lord Vishnu, involving the narration of sacred stories that emphasise truth, gratitude, discipline, and divine grace. It is traditionally performed to express thankfulness and to invite continued prosperity.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'This pujan is performed to seek blessings for prosperity, fulfillment of wishes, family harmony, success in new ventures, and removal of obstacles.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families seeking prosperity | Individuals starting new ventures | Those wanting wish fulfillment | People seeking family harmony',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Purnima (Full Moon), Ekadashi, or any auspicious occasion',
      icon: '🙏'
    }
  ],

  

  'shanti-path': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shanti Paath is a Vedic prayer ritual involving the chanting of sacred peace mantras drawn from the Vedas and Upanishads. It is performed to calm mental, emotional, and environmental disturbances and to establish an atmosphere of harmony and positivity. ',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'Shanti Paath is performed to reduce anxiety, mental unrest, negativity, and unseen disturbances, and to bring steadiness during times of emotional or situational imbalance.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals experiencing stress, restlessness, or anxiety | Families facing frequent disagreements or tension | Before or after major life changes (relocation, career shift, recovery).',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Any day; especially Mondays, Thursdays, or during personal milestones',
      icon: '🙏'
    }
  ],
 'Mangalik-Dosh-Nivaran': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Mangalik Dosh Nivaran Poojan is performed when Mars (Mangal) is positioned unfavourably in the birth chart. The ritual focuses on balancing Mangal’s energy through mantra jaap, sankalp, and havan.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To promote harmony in relationships, reduce impulsive tendencies, and support stability in marriage and partnerships.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals advised after proper kundali analysis | Those preparing for marriage | People seeking emotional balance and patience .',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Tuesdays, astrologically recommended muhurat',
      icon: '🙏'
    }
  ],

  'kaal-sarp-dosh-nivaran': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Kaalsarp Dosh Nivaran Poojan is a Vedic ritual performed when all planets in a birth chart are positioned between Rahu and Ketu. The pooja focuses on balancing this axis through mantra jaap, sankalp, and havan as prescribed in shastras.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to reduce repeated obstacles, delays, mental unrest, or instability, and to restore smoother flow and confidence in one’s efforts.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals with Kaal Sarp Yoga | Those facing repeated obstacles | People with stagnant career growth | Families experiencing persistent problems',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Amavasya, Nag Panchami, or astrologically recommended muhurat.',
      icon: '🙏'
    }
  ],
  'pitra-dosh-nivaran': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Pitra Dosh Nivaran Poojan is a ritual performed to honour ancestors and address unresolved ancestral influences through mantra, sankalp, and offerings.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'To restore ancestral balance, express gratitude, and support harmony, growth, and continuity in the family lineage.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families experiencing repeated patterns across generations | Individuals seeking ancestral blessings | Those advised after astrological assessment.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Amavasya, Pitru Paksha, astrologically recommended muhurat.',
      icon: '🙏'
    }
  ],

  'gaya-ji-shradhh-karm': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shraddh in Gaya Ji involves performing pind daan at Vishnupad Kshetra, believed to grant liberation to ancestral souls through Lord Vishnu’s blessings.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to fulfil ancestral obligations and seek moksha for departed souls.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families with long-pending Shraddh obligations | Those advised by tradition or elders to perform Gaya Shraddh.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Pitru Paksha, Amavasya, specific tithis.',
      icon: '🙏'
    }
  ],
  'kashi-shradh-karm': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Shraddh in Kashi is the performance of ancestral rites on the banks of the Ganga in Varanasi, a moksha kshetra believed to grant peace and liberation to departed souls.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to offer pind daan, tarpan, and prayers for ancestral peace, gratitude, and continuity of blessings across generations.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families wishing to perform Shraddh at a highly sacred location | Those unable to perform Shraddh during Pitru Paksha earlier | Devotees seeking ancestral blessings and closure.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Pitru Paksha, Amavasya, death anniversary (tithi), or advised muhurat.',
      icon: '🙏'
    }
  ],
  'tripindi-shradh': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Tripindi Shraddh is a specialised ancestral ritual performed when the souls of ancestors are believed to be unsettled due to unnatural death, sudden demise, or incomplete Shraddh rites. The ritual symbolically represents three generations of ancestors and aims to provide them peace and proper closure.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'Tripindi Shraddh is performed to restore ancestral balance, remove lingering unrest, and ensure that ancestral blessings flow freely to the present generation. It is often recommended when regular Shraddh rituals have not brought emotional or spiritual relief.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families advised by elders or priests for Tripindi Shraddh | Individuals experiencing repeated stagnation despite efforts | Those wishing to fulfil unresolved ancestral obligations.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Pitru Paksha, Amavasya, or astrologically advised dates.',
      icon: '🙏'
    }
  ],
  'narayan-bali': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Narayan Bali is a highly specialised Shraddh ritual performed for ancestors who passed away due to unnatural, accidental, or premature death. The ritual symbolically offers a Narayan form to the departed soul, aiding in peace and release.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'Narayan Bali is performed to complete rites that could not be performed earlier and to restore spiritual balance. It is considered an act of compassion and responsibility rather than fear or compulsion.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families advised Narayan Bali by traditional priests | Those wishing to complete pending ancestral duties | Individuals seeking spiritual closure and peace for departed souls.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Amavasya, Pitru Paksha, or muhurat advised by Acharya.',
      icon: '🙏'
    }
  ],

  'akhand-ramcharitmanas': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Akhand Ramayan Paath is the uninterrupted recitation of the complete Ramcharitmanas, performed continuously with devotion and discipline. The paath invokes dharma, patience, resilience, and surrender through the divine ideals of Shri Ram.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to create peace and harmony in the household, strengthen moral clarity, and cultivate emotional and spiritual steadiness.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Families seeking peace and harmony | Individuals willing to celebrate a milestone | Devotees of Shri Ram.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Ram Navami, Ekadashi, Tuesdays, Saturdays, or family milestones.',
      icon: '🙏'
    }
  ],

  'sunder-kand-path': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Sunder Kaand Paath is the recitation of the Sunder Kaand chapter of Ramcharitmanas, narrating Hanuman ji’s journey of courage, devotion, and divine service. It is performed to invoke strength, protection, and blessings from Lord Hanuman.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to cultivate courage, remove fear, strengthen faith, and restore confidence during uncertain or demanding phases.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals facing anxiety or uncertainty | Families seeking reassurance and strength | Devotees of Hanuman ji.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Tuesdays, Saturdays, Hanuman Jayanti, Birthdays/milestones.',
      icon: '🙏'
    }
  ],

  'hanuman-chalisa-path': [
    {
      id: 1,
      title: 'What is this Pujan?',
      description: 'Hanuman Chalisa Paath involves structured recitation of the Hanuman Chalisa to invoke courage, discipline, protection, and unwavering faith.',
      icon: '🛡️'
    },
    {
      id: 2,
      title: 'Why is this Pujan performed?',
      description: 'It is performed to build inner strength, remove fear, and cultivate steadiness during uncertainty.',
      icon: '🌿'
    },
    {
      id: 3,
      title: 'Who should consider this Pujan?',
      description: 'Individuals seeking courage and focus | Devotees of Hanuman ji | Families seeking protective blessings.',
      icon: '✨'
    },
    {
      id: 4,
      title: 'Auspicious Timing',
      description: 'Tuesdays, Saturdays, Hanuman Jayanti, Celebrations, Birthdays/milestones.',
      icon: '🙏'
    }
  ],
  
  
  

  
};

